// React core components
import React from "react";

// @mui Material components
import {
    Typography,
    TextField,
    Modal,
    Autocomplete,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Switch from '@mui/material/Switch';

const CouponModal = ({ open, handleClose, couponNewUsers, setCouponNewUsers, allUsers, allCoupons, disabled, setDisabled, expiryDate, setExpiryDate, couponName, setCouponName, selectedCourses, setSelectedCourses,
    allCourses, handleCouponSubmit, handleEditCoupon, handleCouponClone, couponError, setCouponError, currentMode, MODE }) => {
    // Validate coupon name
    const validateName = () => {
        if (couponName.trim() === "") {
            setCouponError("Coupon name is required");
            return false;
        }
        // Ensure that allCoupons is populated and check for duplicate coupon codes
        if (allCoupons && allCoupons.some((coupon) => coupon.code === couponName.trim())) {
            setCouponError("Coupon name already exists");
            return false;
        }
        setCouponError("");
        return true;
    };

    const _handleSave = () => {
        if (currentMode === MODE.EDIT) {
            handleEditCoupon();
        } else if (currentMode === MODE.ADD && validateName()) {
            handleCouponSubmit();
        } else if (currentMode === MODE.CLONE && validateName()) {
            handleCouponClone();
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <div
                style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    backgroundColor: "#fff",
                    padding: "20px",
                    borderRadius: "8px",
                    outline: "none",
                    minWidth: "50vw",
                }}
            >
                <Typography variant="h6" >
                    {currentMode === MODE.ADD && "Add New Coupon"}
                    {currentMode === MODE.EDIT && "Edit Coupon"}
                    {currentMode === MODE.CLONE && "Clone Coupon"}
                </Typography>
                <TextField
                    fullWidth
                    margin="normal"
                    label="Coupon Name"
                    value={couponName}
                    disabled={currentMode == MODE.EDIT}
                    onChange={(e) => { setCouponName(e.target.value) }}
                />
                {couponError && (
                    <Typography variant="caption" color="error">
                        {couponError}
                    </Typography>
                )}
                <Autocomplete
                    multiple
                    options={allCourses ?? []}
                    // how to render the label
                    getOptionLabel={(option) => (option?.app_name ?? "").toString()}
                    // how to compare an option to a value (fixes your error)
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    // make sure value items correspond to options (filter out stale ones)
                    value={(selectedCourses ?? []).filter(
                        (v) => (allCourses ?? []).some((o) => o.id === v.id)
                    )}
                    onChange={(event, newValue) => {
                        // dedupe by id
                        const deduped = Array.from(new Map(newValue.map(v => [v.id, v])).values());
                        setSelectedCourses(deduped);
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label="Select Courses"
                            margin="normal"
                        />
                    )}
                    filterSelectedOptions
                />

                <Autocomplete
                    multiple
                    options={allUsers ?? []}
                    getOptionLabel={(option) => (option?.email ?? "").toString()}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    disableCloseOnSelect
                    // keep only values that still exist in options
                    value={(couponNewUsers ?? []).filter(
                        (v) => (allUsers ?? []).some((o) => o.id === v.id)
                    )}
                    onChange={(event, newValue) => {
                        // dedupe by id
                        const deduped = Array.from(new Map(newValue.map(u => [u.id, u])).values());
                        setCouponNewUsers(deduped);
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label="Select Users"
                            margin="normal"
                        />
                    )}
                    filterSelectedOptions
                />


                <TextField
                    id="date"
                    label="Expiry Date"
                    margin="normal"
                    type="date"
                    defaultValue={new Date(expiryDate).toISOString().split("T")[0]} // Ensure correct format
                    onChange={(e) => setExpiryDate(new Date(e.target.value))} // Store as Date object
                    sx={{ width: 220 }}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    inputProps={{
                        min: new Date().toISOString().split("T")[0], // Disables past dates
                    }}
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={!disabled}  // Use checked instead of defaultValue
                            onChange={() => setDisabled(!disabled)}  // Toggle the state on change
                        />
                    }
                    label={disabled ? "Activate the disabled access code" : "Disable the access code"}
                    labelPlacement="start"
                    sx={{ display: "flex", justifyContent: "start", marginLeft: 0 }}
                />

                <MDBox mt={2} display="flex" justifyContent="flex-end">
                    <MDButton
                        onClick={handleClose}
                        color="secondary"
                        style={{ marginRight: "10px" }}
                    >
                        Cancel
                    </MDButton>
                    <MDButton
                        color="success"
                        onClick={() => { _handleSave() }}
                    >
                        Save
                    </MDButton>
                </MDBox>
            </div>
        </Modal>
    );
};

export default CouponModal; // Ensure this line exists
